import { useEffect, useRef, useState } from "react";
import { Button, Input, Spin, message } from "antd";
import {
  CloseOutlined,
  MinusOutlined,
  PlusOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import apiClient from "../actions/api";

const { TextArea } = Input;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const errorText = (error, fallback) =>
  error?.response?.data?.errors?.[0]?.msg ||
  error?.response?.data?.message ||
  fallback;
const normalizeCompanyName = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
const normalizeResumeText = (value) =>
  String(value || "")
    .replace(/^[\s•·▪◦‣⁃\-*–—]+/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const ProfileResumePreview = ({ draft, setDraft, companyName, setCompanyName }) => {
  const summary = draft.summary_changes || {};
  const skills = draft.skills_changes || {};
  const employment = draft.employment_changes || [];
  const replacements = new Map();

  employment.forEach((job) => {
    if (job.original_role) {
      replacements.set(
        normalizeResumeText(job.original_role),
        job.optimized_role || job.original_role,
      );
    }
    (job.original_bullets || []).forEach((bullet, index) =>
      replacements.set(
        normalizeResumeText(bullet),
        (job.optimized_bullets || [])[index] || bullet,
      ),
    );
  });
  (skills.original_skills || []).forEach((line, index) =>
    replacements.set(
      normalizeResumeText(line),
      (skills.optimized_skills || [])[index] || line,
    ),
  );
  if (summary.original_summary) {
    replacements.set(
      normalizeResumeText(summary.original_summary),
      summary.optimized_summary || summary.original_summary,
    );
  }

  const updateEmployment = (employmentIndex, field, value, itemIndex) => {
    setDraft((current) => ({
      ...current,
      employment_changes: (current.employment_changes || []).map((job, index) => {
        if (index !== employmentIndex) return job;
        if (itemIndex == null) return { ...job, [field]: value };
        const values = [...(job[field] || [])];
        values[itemIndex] = value;
        return { ...job, [field]: values };
      }),
    }));
  };

  return (
    <div className="profile-resume-review">
      <div className="profile-review-fields">
        <div className="resume-window-grid">
          <div>
            <label>Company name</label>
            <Input value={companyName} onChange={(event) => setCompanyName(event.target.value)} />
          </div>
          {["role_title", "salary_range", "job_type"].map((field) => (
            <div key={field}>
              <label>{field.replace(/_/g, " ")}</label>
              <Input
                value={draft[field] || ""}
                onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
              />
            </div>
          ))}
        </div>
        <div className="profile-review-card">
          <strong>Optimized summary</strong>
          <TextArea
            rows={5}
            value={summary.optimized_summary || ""}
            onChange={(event) => setDraft((current) => ({
              ...current,
              summary_changes: { ...(current.summary_changes || {}), optimized_summary: event.target.value },
            }))}
          />
        </div>
        {employment.map((job, employmentIndex) => (
          <div className="profile-review-card" key={`${job.employment_id || job.employer}-${employmentIndex}`}>
            <strong>{job.job_title || "Employment"}{job.employer ? ` — ${job.employer}` : ""}</strong>
            <label>Optimized role</label>
            <Input
              value={job.optimized_role || ""}
              onChange={(event) => updateEmployment(employmentIndex, "optimized_role", event.target.value)}
            />
            {(job.optimized_bullets || []).map((bullet, bulletIndex) => (
              <div key={bulletIndex}>
                <label>Optimized bullet {bulletIndex + 1}</label>
                <TextArea
                  rows={2}
                  value={bullet}
                  onChange={(event) => updateEmployment(employmentIndex, "optimized_bullets", event.target.value, bulletIndex)}
                />
              </div>
            ))}
          </div>
        ))}
        <div className="profile-review-card">
          <strong>Optimized skills</strong>
          {(skills.optimized_skills || []).map((skill, index) => (
            <Input
              key={index}
              value={skill}
              onChange={(event) => setDraft((current) => {
                const values = [...(current.skills_changes?.optimized_skills || [])];
                values[index] = event.target.value;
                return { ...current, skills_changes: { ...(current.skills_changes || {}), optimized_skills: values } };
              })}
            />
          ))}
        </div>
      </div>
      <section className="profile-document-preview">
        <div className="profile-document-preview-heading">
          <strong>Full resume preview</strong>
          <span>Original text and tailored replacements</span>
        </div>
        <div className="profile-document-page">
          {(draft.original_resume_lines || []).map((line, index) => {
            const replacement = replacements.get(normalizeResumeText(line));
            return replacement && normalizeResumeText(replacement) !== normalizeResumeText(line) ? (
              <div className="profile-line-changed" key={index}>
                <p className="profile-line-removed">{line}</p>
                <p className="profile-line-added">{replacement}</p>
              </div>
            ) : <p key={index}>{line || "\u00a0"}</p>;
          })}
        </div>
      </section>
    </div>
  );
};

export default function JobResumeWindow({
  item,
  active,
  user,
  profileId,
  sourceType,
  resumeProfileId,
  onActivate,
  onClose,
  onFinalized,
}) {
  const [stage, setStage] = useState("build");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bid, setBid] = useState(null);
  const [draft, setDraft] = useState(null);
  const [documentCompanyName, setDocumentCompanyName] = useState(item.company);
  const autoGenerationStarted = useRef(false);

  const setField = (field, value) =>
    setDraft((old) => ({ ...old, [field]: value }));
  const setExperience = (key, value) =>
    setDraft((old) => ({
      ...old,
      experiences: { ...old.experiences, [key]: value },
    }));
  const setSkill = (category, index, value) =>
    setDraft((old) => ({
      ...old,
      skills: {
        ...old.skills,
        [category]: old.skills[category].map((skill, current) =>
          current === index ? value : skill,
        ),
      },
    }));
  const changeList = (type, key, updater) =>
    setDraft((old) => ({
      ...old,
      [type]: {
        ...old[type],
        [key]: updater([...(old[type][key] || [])]),
      },
    }));

  const getJobUrl = async () => {
    const { data } = await apiClient.get("/job-board/job-url", {
      params: {
        jobId: item.id,
        profileId,
        companyName: item.company,
        position: item.position,
      },
    });
    if (!data?.url) throw new Error("The job URL is unavailable.");
    return data.url;
  };

  const generateDraft = async () => {
    if (sourceType === "template" && !user?.template_url) {
      setError("Please upload a resume template in your profile first.");
      return;
    }
    if (sourceType === "resume_document" && !resumeProfileId) {
      setError("Please select a resume profile first.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const jobUrl = await getJobUrl();
      const { data } = await apiClient.post("/bids/gen-draft", {
        user: user._id,
        job_url: jobUrl,
        job_desc: item.description,
        source_type: sourceType,
        resume_profile_id:
          sourceType === "resume_document" ? resumeProfileId : undefined,
      });
      const result =
        typeof data?.result === "string"
          ? JSON.parse(data.result)
          : data?.result;
      if (!result || !data?.bid) throw new Error("Draft data is unavailable.");
      const companyName =
        normalizeCompanyName(result.company_name) ===
        normalizeCompanyName(item.company)
          ? result.company_name
          : item.company;
      setBid(data.bid);
      setDraft({
        ...result,
        company_name: companyName,
        skills: result.skills || {},
        experiences: result.experiences || {},
      });
      setStage("preview");
    } catch (requestError) {
      setError(
        errorText(
          requestError,
          requestError.message || "Unable to generate resume draft.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoGenerationStarted.current) return;
    autoGenerationStarted.current = true;
    generateDraft();
    // A newly opened company window generates exactly one initial draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finalize = async () => {
    setLoading(true);
    setError("");
    try {
      const endpoint =
        sourceType === "resume_document"
          ? "/bids/gen-resume-document"
          : "/bids/gen-resume";
      const payload = {
        company_name: String(
          sourceType === "resume_document"
            ? documentCompanyName
            : draft.company_name || item.company,
        ).replace(
          /\.$/,
          "",
        ),
        developer_title: draft.developer_title || "",
        role_title: draft.role_title || "",
        salary_range: draft.salary_range || "",
        job_type: draft.job_type || "",
        summary: draft.summary || "",
        skills: draft.skills,
        experiences: draft.experiences,
        bid,
        user,
      };
      if (sourceType === "resume_document") payload.optimization = draft;
      const { data } = await apiClient.post(endpoint, payload);
      if (data?.status !== "success")
        throw new Error("Unable to finalize resume.");
      const response = await fetch(
        `${API_BASE_URL}/job/description/${data.downloadPDFLink}`,
      );
      if (response.ok) {
        const blobUrl = URL.createObjectURL(await response.blob());
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = data.pdf_filename || "resume.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(blobUrl);
      }
      message.success(data.msg || "Resume generated successfully.");
      onFinalized?.(data.balance);
      onClose(item.id);
    } catch (requestError) {
      setError(
        errorText(
          requestError,
          requestError.message || "Unable to finalize resume.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const experienceKeys = Object.keys(draft?.experiences || {}).sort(
    (left, right) => {
      const leftRole = /^role\d+$/.test(left);
      const rightRole = /^role\d+$/.test(right);
      if (leftRole !== rightRole) return leftRole ? -1 : 1;
      return (
        (Number(left.match(/\d+/)?.[0]) || 0) -
        (Number(right.match(/\d+/)?.[0]) || 0)
      );
    },
  );

  return (
    <section className={`job-resume-window ${sourceType === "resume_document" ? "profile-workflow" : ""} ${active ? "active" : "collapsed"}`}>
      <header onClick={() => onActivate(item.id)}>
        <div>
          <strong>{item.company}</strong>
          <small>
            {stage === "preview" ? "Resume preview" : "Build resume"}
          </small>
        </div>
        <span className="window-controls">
          <button type="button" title={active ? "Minimize" : "Open"}>
            {active ? <MinusOutlined /> : <PlusOutlined />}
          </button>
          <button
            type="button"
            title="Close"
            onClick={(event) => {
              event.stopPropagation();
              onClose(item.id);
            }}
          >
            <CloseOutlined />
          </button>
        </span>
      </header>
      {active && (
        <div className="job-resume-window-body">
          {loading && (
            <div className="resume-window-loading">
              <Spin />
            </div>
          )}
          {error && <div className="resume-window-error">{error}</div>}
          {stage === "build" ? (
            <>
              <label>Job URL</label>
              <Input
                value="Resolved securely from this job when generated"
                disabled
              />
              <label>Job Description</label>
              <TextArea value={item.description} rows={14} readOnly />
              <Button
                type="primary"
                icon={<RocketOutlined />}
                onClick={generateDraft}
                loading={loading}
                block
              >
                Generate Resume Draft
              </Button>
            </>
          ) : (
            <>
              <div className="resume-window-sticky-action">
                <Button
                  type="primary"
                  onClick={finalize}
                  loading={loading}
                  block
                >
                  Finalize Resume
                </Button>
              </div>
              {sourceType === "resume_document" ? (
                <ProfileResumePreview
                  draft={draft}
                  setDraft={setDraft}
                  companyName={documentCompanyName}
                  setCompanyName={setDocumentCompanyName}
                />
              ) : (
              <>
              <div className="resume-window-grid">
                {[
                  ["Company", "company_name"],
                  ["Company Title", "role_title"],
                  ["Role Title", "developer_title"],
                  ["Job Type", "job_type"],
                  ["Salary Range", "salary_range"],
                ].map(([label, field]) => (
                  <div key={field}>
                    <label>{label}</label>
                    <Input
                      value={draft[field] || ""}
                      onChange={(event) => setField(field, event.target.value)}
                    />
                  </div>
                ))}
              </div>
              <label>Summary</label>
              <TextArea
                rows={4}
                value={draft.summary || ""}
                onChange={(event) => setField("summary", event.target.value)}
              />
              {experienceKeys.map((key) =>
                Array.isArray(draft.experiences[key]) ? (
                  <div key={key}>
                    <label>{key.replace(/(\d+)/, " $1")}</label>
                    {draft.experiences[key].map((value, index) => (
                      <div className="resume-window-entry" key={index}>
                        <TextArea
                          rows={2}
                          value={value}
                          onChange={(event) => {
                            const values = [...draft.experiences[key]];
                            values[index] = event.target.value;
                            setExperience(key, values);
                          }}
                        />
                        <Button
                          size="small"
                          danger
                          onClick={() =>
                            changeList("experiences", key, (values) =>
                              values.filter((_, current) => current !== index),
                            )
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      size="small"
                      type="link"
                      onClick={() =>
                        changeList("experiences", key, (values) => [
                          ...values,
                          "",
                        ])
                      }
                    >
                      + Add entry
                    </Button>
                  </div>
                ) : (
                  <div key={key}>
                    <label>{key.replace(/(\d+)/, " $1")}</label>
                    <Input
                      value={draft.experiences[key] || ""}
                      onChange={(event) =>
                        setExperience(key, event.target.value)
                      }
                    />
                  </div>
                ),
              )}
              {Object.entries(draft.skills).map(([category, values]) => (
                <div key={category}>
                  <label>{category.replace(/_/g, " ")}</label>
                  {(Array.isArray(values) ? values : []).map((value, index) => (
                    <div className="resume-window-entry" key={index}>
                      <Input
                        value={value}
                        onChange={(event) =>
                          setSkill(category, index, event.target.value)
                        }
                      />
                      <Button
                        size="small"
                        danger
                        onClick={() =>
                          changeList("skills", category, (items) =>
                            items.filter((_, current) => current !== index),
                          )
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    size="small"
                    type="link"
                    onClick={() =>
                      changeList("skills", category, (items) => [...items, ""])
                    }
                  >
                    + Add skill
                  </Button>
                </div>
              ))}
              </>
              )}
              <Button type="primary" onClick={finalize} loading={loading} block>
                Finalize Resume
              </Button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
